import { Box, Button, Collapse, Divider, HStack, Stack, Text, useDisclosure } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SeQuestionDto, SeQuestionMessageDto, SeQuestionStatus } from "../../../autogen/bff-api";
import { AttachmentList } from "../../../common/components/communication/AttachmentList";
import {
  formatDateTime,
  personName,
} from "../../../common/components/communication/communicationUtils";
import { QuestionStatusBadge } from "../../../common/components/communication/QuestionStatusBadge";
import { TipTap } from "../../../common/input/TipTap/TipTap";
import { AnswerForm } from "./AnswerForm";
import { PublishInformationModal } from "./PublishInformationModal";

const QuestionDialogMessage = ({
  eventId,
  message,
  isBuyerSide,
}: {
  eventId: string;
  message: SeQuestionMessageDto;
  isBuyerSide: boolean;
}) => (
  <Box
    borderWidth="1px"
    borderColor={isBuyerSide ? "smPrimary" : "smBorder"}
    rounded="md"
    p="3"
    bg={isBuyerSide ? "smPrimarySofter" : "smBackgroundSecondary"}
  >
    <Text fontSize="xs" color={isBuyerSide ? "smChipTealFg" : "smMuted"} mb="1">
      {personName(message.sentBy)} · {message.sentByOrgName} · {formatDateTime(message.sentAt)}
    </Text>
    <TipTap content={message.content} editable={false} maxHeight="48" />
    {message.attachments && message.attachments.length > 0 && (
      <AttachmentList eventId={eventId} attachments={message.attachments} />
    )}
  </Box>
);

export const BuyerQuestionCard = ({ eventId, question }: { eventId: string; question: SeQuestionDto }) => {
  const { t } = useTranslation();
  const { isOpen, onToggle } = useDisclosure({
    defaultIsOpen: question.status === SeQuestionStatus.Unanswered,
  });
  const publishModal = useDisclosure();
  const [answerDraft, setAnswerDraft] = useState("");

  return (
    <Box borderWidth="1px" borderColor="smBorder" rounded="lg" bg="smBackground">
      <HStack px="4" py="3" justify="space-between" cursor="pointer" onClick={onToggle}>
        <Stack spacing="0.5">
          <HStack>
            <Text fontWeight="semibold">{question.subject}</Text>
            <QuestionStatusBadge status={question.status} />
          </HStack>
          <Text fontSize="xs" color="smMuted">
            {personName(question.askedByPerson)} · {question.askedByOrgName} · {formatDateTime(question.createdAt)}
          </Text>
        </Stack>
      </HStack>

      <Collapse in={isOpen} animateOpacity>
        <Divider borderColor="smBorder" />
        <Stack px="4" py="4" spacing="4">
          <Box bg="smBackground" rounded="md" p="3">
            <TipTap content={question.content} editable={false} maxHeight="96" />
            <AttachmentList eventId={eventId} attachments={question.attachments} />
          </Box>

          {question.messages && question.messages.length > 0 && (
            <Stack spacing="2">
              {question.messages.map((msg, index) => (
                <QuestionDialogMessage
                  key={`${msg.sentAt}-${index}`}
                  eventId={eventId}
                  message={msg}
                  isBuyerSide={msg.sentByOrganizationId !== question.supplierOrganizationId}
                />
              ))}
            </Stack>
          )}

          {question.answer && (
            <Box borderWidth="1px" borderColor="smBorder" rounded="md" p="3">
              <Text fontSize="sm" fontWeight="semibold" color="smPrimary" mb="1">
                {t("Answer")} · {personName(question.answer.answeredBy)} ·{" "}
                {formatDateTime(question.answer.answeredAt)}
              </Text>
              <TipTap content={question.answer.content} editable={false} maxHeight="96" />
              <AttachmentList eventId={eventId} attachments={question.answer.attachments} />
            </Box>
          )}

          {question.status === SeQuestionStatus.Unanswered && (
            <AnswerForm
              eventId={eventId}
              questionId={question.id}
              onAnswerChange={setAnswerDraft}
              trailingAction={
                <Button size="sm" colorScheme="teal" variant="outline" onClick={publishModal.onOpen}>
                  {t("Anonymize and send to all")}
                </Button>
              }
            />
          )}

          {question.status === SeQuestionStatus.Answered && (
            <Box>
              <Button size="sm" colorScheme="teal" onClick={publishModal.onOpen}>
                {t("Anonymize and send to all")}
              </Button>
            </Box>
          )}

          {question.status === SeQuestionStatus.Published && (
            <Text fontSize="sm" color="smMuted">
              {t("Sent to all tenderers")}
            </Text>
          )}
        </Stack>
      </Collapse>

      <PublishInformationModal
        eventId={eventId}
        fromQuestion={question}
        initialAnswer={answerDraft}
        isOpen={publishModal.isOpen}
        onClose={publishModal.onClose}
      />
    </Box>
  );
};
